import { MILESTONE_THRESHOLDS } from '@common/constants';
import { Box, Chip, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

interface Props {
    title: string;
    value: number | null;
    subtitle: string;
    tooltip?: string;
    isConnect?: boolean;
    momentumLabel?: string | null;
    milestoneType?: 'hours' | 'intakes';
    loading?: boolean;
    /** Optional unit rendered after the primary value, e.g. "hours". */
    unit?: string;
    /** Optional second figure rendered alongside the primary value so both "pop". */
    secondaryValue?: number | null;
    secondaryUnit?: string;
    secondaryFormat?: 'currency' | 'number';
}

function formatCurrency(value: number): string {
    return value.toLocaleString('en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 0,
    });
}

function useMilestone(value: number | null, type: 'hours' | 'intakes' | undefined): string | null {
    if (value == null || !type) return null;
    const thresholds =
        type === 'hours'
            ? MILESTONE_THRESHOLDS.timeSavedHours
            : MILESTONE_THRESHOLDS.intakesCompleted;
    let highest: number | null = null;
    for (const t of thresholds) {
        if (value >= t) highest = t;
    }
    if (highest == null) return null;
    return type === 'hours'
        ? `✦ ${highest.toLocaleString()} hrs`
        : `✦ ${highest.toLocaleString()} intakes`;
}

function useCountUp(target: number, shouldAnimate: boolean): number {
    const [displayed, setDisplayed] = useState(shouldAnimate ? 0 : target);
    const animating = useRef(false);

    useEffect(() => {
        if (!shouldAnimate || animating.current) {
            setDisplayed(target);
            return;
        }
        if (
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            setDisplayed(target);
            return;
        }
        animating.current = true;
        const duration = 700;
        const start = performance.now();
        const tick = (now: number) => {
            const t = Math.min((now - start) / duration, 1);
            const eased = 1 - (1 - t) ** 3;
            setDisplayed(Math.round(eased * target));
            if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [target, shouldAnimate]);

    return displayed;
}

// One animation per session
const hasAnimated = (() => {
    try {
        return sessionStorage.getItem('dashboard_hero_animated') === '1';
    } catch {
        return true;
    }
})();

export default function HeroCard({
    title,
    value,
    subtitle,
    tooltip,
    isConnect = false,
    momentumLabel,
    milestoneType,
    loading = false,
    unit,
    secondaryValue,
    secondaryUnit,
    secondaryFormat = 'number',
}: Props) {
    const theme = useTheme();
    const milestone = useMilestone(value, milestoneType);
    const shouldAnimate = !hasAnimated && value != null;
    const displayed = useCountUp(value ?? 0, shouldAnimate);
    const hasSecondary = secondaryValue != null;
    const shouldAnimateSecondary = !hasAnimated && hasSecondary;
    const displayedSecondary = useCountUp(secondaryValue ?? 0, shouldAnimateSecondary);

    useEffect(() => {
        if (shouldAnimate) {
            try {
                sessionStorage.setItem('dashboard_hero_animated', '1');
            } catch {
                // ignore
            }
        }
    }, [shouldAnimate]);

    const cardSx = {
        p: 2.5,
        borderRadius: 2,
        border: isConnect
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
        backgroundColor: isConnect ? `${theme.palette.primary.main}0D` : 'white',
        boxShadow: isConnect
            ? `0 6px 20px ${theme.palette.primary.main}26`
            : '0 1px 3px rgba(16, 24, 40, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minHeight: 140,
        position: 'relative',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isConnect
                ? `0 10px 28px ${theme.palette.primary.main}33`
                : '0 4px 12px rgba(16, 24, 40, 0.12)',
        },
    };

    if (loading) {
        return (
            <Box sx={cardSx}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" height={40} />
                <Skeleton variant="text" width="80%" />
            </Box>
        );
    }

    const formattedValue = isConnect
        ? `${(displayed ?? 0).toLocaleString()} minutes`
        : value !== null
          ? `${(displayed ?? 0).toLocaleString()}${unit ? ` ${unit}` : ''}`
          : '—';

    const formattedSecondary = hasSecondary
        ? `${
              secondaryFormat === 'currency'
                  ? formatCurrency(displayedSecondary)
                  : (displayedSecondary ?? 0).toLocaleString()
          }${secondaryUnit ? ` ${secondaryUnit}` : ''}`
        : null;

    const bigValueSx = {
        fontWeight: 700,
        fontSize: 'clamp(1.4rem, 2vw, 2rem)',
        color: theme.palette.text.primary,
    } as const;

    return (
        <Box sx={cardSx}>
            {milestone && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontSize: '11px',
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                    }}
                >
                    {milestone}
                </Box>
            )}
            <Typography
                variant="overline"
                sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.2,
                    fontSize: '10px',
                    letterSpacing: '0.08em',
                }}
            >
                {title}
                {isConnect && (
                    <>
                        {' — '}
                        <Box
                            component="span"
                            sx={{
                                fontStyle: 'italic',
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                letterSpacing: '0.04em',
                            }}
                        >
                            Lexo Connect
                        </Box>
                    </>
                )}
            </Typography>
            <Tooltip title={tooltip ?? ''} placement="top" disableHoverListener={!tooltip}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        columnGap: 1,
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            ...bigValueSx,
                            whiteSpace: hasSecondary ? 'normal' : 'nowrap',
                        }}
                    >
                        {formattedValue}
                        {hasSecondary ? ',' : ''}
                    </Typography>
                    {formattedSecondary && (
                        <Typography
                            variant="h3"
                            sx={{ ...bigValueSx, color: theme.palette.primary.main }}
                        >
                            {formattedSecondary}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {subtitle}
            </Typography>
            {isConnect && (
                <Tooltip
                    arrow
                    placement="bottom-start"
                    title={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                                <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 700 }}>
                                    Lexo Connect
                                </Box>{' '}
                                is our integration across conveyancing applications that
                                automatically populates matter information for you — no more
                                retyping.
                            </Typography>
                            <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                                Interested? Ask your account manager to get set up.
                            </Typography>
                            <Link
                                href="https://lexo.ai/connect"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: 13, color: 'inherit', fontWeight: 600 }}
                            >
                                Learn more at lexo.ai/connect →
                            </Link>
                        </Box>
                    }
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: 300,
                                p: 1.5,
                                backgroundColor: '#1f2937',
                            },
                        },
                        arrow: { sx: { color: '#1f2937' } },
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            cursor: 'help',
                            alignSelf: 'flex-start',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: '2px',
                        }}
                    >
                        What is Lexo Connect?
                    </Typography>
                </Tooltip>
            )}
            {momentumLabel && (
                <Chip
                    label={momentumLabel}
                    size="small"
                    sx={{
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        backgroundColor: '#e6f4ea',
                        color: '#1e7e34',
                        fontWeight: 600,
                        fontSize: '11px',
                        height: 20,
                    }}
                />
            )}
        </Box>
    );
}
